import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import { ErrorBoundary } from '@allenai/varnish';
import message from 'antd/es/message';
import Descriptions from 'antd/es/descriptions';
import Button from 'antd/es/button';

import { EditOutlined, LeftOutlined } from '@ant-design/icons';

import { PageHeader, columnRender } from '../components';
import { getAsset } from '../api';

// Load types and schema from OpenAPI. See project README.md for info on how these files are formed.
import { components } from '../../build/openapiTypes';
import { UiSchemaKey, getCategoryAndAssetDetails } from '../utils/util';
import { MaxWidthContainer } from '../components/Layout';

interface Params {
    category: string;
    id: string;
}

const AssetDetailsImpl = ({ match, history }: RouteComponentProps<Params>) => {
    const { categoryData, assetData, assetSchemaProperties, assetNameFromType } =
        getCategoryAndAssetDetails(match.params.category);

    // grab ts type for the asset
    // omitting string because we want only objects
    type Asset = Omit<components['schemas'][typeof assetNameFromType], string>;

    const [data, setData] = useState<Asset>();
    useEffect(() => {
        if (match.params.id) {
            getAsset<Asset>(match.params.category, match.params.id)
                .then((d) => setData(d))
                .catch((e) => message.error(`Error loading data: ${e.message}`));
        }
    }, [match]);

    return (
        <MaxWidthContainer>
            <PageHeader
                iconName={categoryData.iconName}
                iconType={categoryData.iconType}
                label={assetData.label}
                subLabel="Details">
                <Buttons>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => history.push(`${location.pathname}/edit`)}>
                        Edit
                    </Button>
                    <Button
                        type="link"
                        icon={<LeftOutlined aria-hidden />}
                        onClick={() => history.goBack()}>
                        Back
                    </Button>
                </Buttons>
            </PageHeader>
            {data ? (
                <>
                    <Descriptions bordered column={1}>
                        {Object.keys(assetSchemaProperties).map((d) => (
                            <Descriptions.Item key={d} label={assetSchemaProperties[d].title}>
                                <EditRow>
                                    {columnRender(
                                        assetSchemaProperties[d],
                                        UiSchemaKey.WidgetDetail
                                    )((data as any)[d], data)}
                                    <Button
                                        type="text"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            history.push(`${location.pathname}/edit`)
                                        }></Button>
                                </EditRow>
                            </Descriptions.Item>
                        ))}
                    </Descriptions>
                </>
            ) : null}
        </MaxWidthContainer>
    );
};

export const AssetDetails = (props: RouteComponentProps<Params>) => {
    // TODO: Github #108, move error boundary so we dont need to set it inside each new page
    return (
        <ErrorBoundary key={props.match.params.category}>
            <AssetDetailsImpl {...props} />
        </ErrorBoundary>
    );
};

const Buttons = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: ${({ theme }) => theme.spacing.xs};
`;

const EditRow = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
`;
